import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Globe, MessageSquare, FileCheck } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * iter245 — Services en ligne du client : suivi des dossiers + messagerie.
 * La messagerie dépend du suivi (on ne peut pas discuter d'un dossier qu'on ne voit pas).
 * Tant que le suivi est coupé, l'OR n'affiche ni partage de suivi ni fil de discussion.
 */
export const ClientSuiviServicesCard = ({ client, onUpdated }) => {
  const [saving, setSaving] = useState(null);
  const suivi = !!client?.suivi_actif;
  const messagerie = !!client?.suivi_messagerie_actif;
  const validation = client?.suivi_validation_devis || "code";

  const save = async (patch, key) => {
    setSaving(key);
    try {
      const { data } = await api.patch(`/clients/${client.id}`, patch);
      onUpdated?.(data);
      toast.success("Services en ligne mis à jour");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Modification impossible");
    } finally {
      setSaving(null);
    }
  };

  return (
    <Card className="p-5 border-gray-200" data-testid="client-suivi-services">
      <h2 className="font-heading font-semibold text-gray-900 mb-1">Suivi en ligne</h2>
      <p className="text-xs text-gray-500 mb-4">
        Donne à ce client un accès web à l&apos;avancement de ses dossiers. Sans activation,
        aucun lien de suivi ni discussion n&apos;est proposé sur ses OR.
      </p>

      <div className="flex items-start gap-3 py-2">
        <Switch
          checked={suivi}
          disabled={saving === "suivi"}
          onCheckedChange={(v) => save({ suivi_actif: v }, "suivi")}
          data-testid="client-suivi-toggle"
        />
        <span className="min-w-0">
          <span className="text-sm font-medium text-gray-900 inline-flex items-center gap-1.5">
            <Globe className="w-3.5 h-3.5 text-gray-400" /> Suivi des dossiers
          </span>
          <span className="block text-xs text-gray-500">
            Lien privé vers le statut de ses véhicules, devis et photos partagées
          </span>
        </span>
      </div>

      <div className={`flex items-start gap-3 py-2 ${suivi ? "" : "opacity-50"}`}>
        <Switch
          checked={messagerie}
          disabled={!suivi || saving === "messagerie"}
          onCheckedChange={(v) => save({ suivi_messagerie_actif: v }, "messagerie")}
          data-testid="client-messagerie-toggle"
        />
        <span className="min-w-0">
          <span className="text-sm font-medium text-gray-900 inline-flex items-center gap-1.5">
            <MessageSquare className="w-3.5 h-3.5 text-gray-400" /> Messagerie
          </span>
          <span className="block text-xs text-gray-500">
            {suivi
              ? "Le client peut écrire au garage depuis son suivi (texte et photos)"
              : "Nécessite le suivi des dossiers"}
          </span>
        </span>
      </div>

      {/* iter263 — comment ce client valide un devis depuis son lien de suivi */}
      <div className={`pt-3 mt-2 border-t border-gray-100 ${suivi ? "" : "opacity-50"}`}>
        <Label className="text-sm font-medium text-gray-900 inline-flex items-center gap-1.5 mb-1">
          <FileCheck className="w-3.5 h-3.5 text-gray-400" /> Validation des devis
        </Label>
        <Select
          value={validation}
          disabled={!suivi || saving === "validation"}
          onValueChange={(v) => save({ suivi_validation_devis: v }, "validation")}
        >
          <SelectTrigger className="mt-1" data-testid="client-validation-devis">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="code" data-testid="client-validation-code">
              N° d&apos;autorisation (flottes, assurances)
            </SelectItem>
            <SelectItem value="bouton" data-testid="client-validation-bouton">
              Bouton « Valider le devis »
            </SelectItem>
            <SelectItem value="aucun" data-testid="client-validation-aucun">
              Aucune validation en ligne
            </SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-gray-500 mt-1.5">
          {validation === "code"
            ? "Le client saisit son n° d'autorisation : le devis passe alors en « Devis accepté »."
            : validation === "bouton"
              ? "Le client valide d'un clic : le devis passe en « Devis accepté »."
              : "Le client consulte son devis mais ne peut pas le valider en ligne."}
        </p>
      </div>
    </Card>
  );
};

export default ClientSuiviServicesCard;
